import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

import Spinner from 'components/Spinner'
import FormattedMessage from 'components/FormattedMessage'
import Confirmation from 'components/Layer/Confirmation'

import { sanitizeGraphQLErrors, showErrors } from 'helpers/errors'
import useBool from 'hooks/bool'

import { useProviderCalendarsQuery, useBlockTimeSlotMutation } from './graphql.generated'
import CronofyAvailabilityViewer from './CronofyAvailabilityViewer'

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = (props) => {
  const { providerId } = props

  const intl = useIntl()
  const { loading, data } = useProviderCalendarsQuery({ variables: { id: providerId } })
  const provider = data?.provider
  const calendarToRender = provider?.cronofyExternalCalendar || provider?.cronofyInternalCalendar
  const [blockTimeSlot] = useBlockTimeSlotMutation()
  const [isAccessOverlayVisible, showAccessOverlay, hideAccessOverlay] = useBool(false)

  const onSlotSelected = useCallback((startTime, endTime) => {
    if (!calendarToRender) { return }
    if (calendarToRender.scope !== 'read_write') {
      showAccessOverlay()

      return
    }

    blockTimeSlot({
      variables: {
        id: calendarToRender.id,
        startTime,
        endTime,
      },
    })
      .catch((error) => showErrors(sanitizeGraphQLErrors(error.graphQLErrors, { intl })))
  }, [calendarToRender])

  if (loading) {
    return <Spinner />
  }

  if (!calendarToRender) { return null }

  return (
    <section className="provider-profile-page__content__section">
      <h3 className="provider-profile-page__content__headline">
        <FormattedMessage id="providerProfilePage.mainContent.availability" />
      </h3>

      <CronofyAvailabilityViewer
        calendar={calendarToRender}
        onSlotSelected={onSlotSelected}
      />

      {isAccessOverlayVisible && (
        <Confirmation
          title={<FormattedMessage id="providerProfilePage.mainContent.availability.noWriteAccess" />}
          submitHandler={hideAccessOverlay}
          closeHandler={hideAccessOverlay}
          submitTranslationKey="common.ok"
        />
      )}
    </section>
  )
}

interface AvailabilityCalendarProps {
  providerId: string
}

export default AvailabilityCalendar
